import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UsersTab } from '../components/admin/UsersTab';
import { ListingsTab } from '../components/admin/ListingsTab';
import { TokensTab } from '../components/admin/TokensTab';
import { BlogsTab } from '../components/admin/BlogsTab';
import { CategoriesTab } from '../components/admin/CategoriesTab';
import { NewsletterTab } from '../components/admin/NewsletterTab';
import { PricingTab } from '../components/admin/PricingTab';
import { AiSettingsTab } from '../components/admin/AiSettingsTab';
import { SupportRequestsTab } from '../components/admin/SupportRequestsTab';

export function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAppStore();
  const initialTab = searchParams.get('tab') || 'listings';
  const [activeTab, setActiveTab] = useState<'users' | 'listings' | 'tokens' | 'blogs' | 'categories' | 'newsletter' | 'pricing' | 'ai' | 'support'>(initialTab as any);

  // Sync tab from URL params (e.g. notification links)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['users', 'listings', 'tokens', 'blogs', 'categories', 'newsletter', 'pricing', 'ai', 'support'].includes(tabParam)) {
      setActiveTab(tabParam as any);
    }
  }, [searchParams]);

  // Admin access ONLY via 'role' field (checked from DB, not user_type)
  // user_type is account type (fizicko_lice, pravno_lice)
  // role is system role (user, admin) - verified server-side on every API call
  const isAdmin = user?.role === 'admin';
  
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8"
          >
            <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Pristup odbijen</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Nemate dozvolu za pristup admin panelu. Potreban je admin nalog.
            </p>
            <button
              onClick={() => navigate('/prijava')}
              className="mt-6 px-6 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
            >
              Prijavi se
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'listings', label: 'Oglasi' },
    { id: 'users', label: 'Korisnici' },
    { id: 'tokens', label: 'Tokeni & Promo' },
    { id: 'blogs', label: 'Blog' },
    { id: 'categories', label: 'Kategorije' },
    { id: 'newsletter', label: 'Newsletter' },
    { id: 'pricing', label: 'Cenovnik' },
    { id: 'ai', label: '🤖 AI Asistent' },
    { id: 'support', label: '📩 Zahtevi' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Admin Panel</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
              </div>
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-2 mb-6 overflow-x-auto">
            <div className="flex space-x-2 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="min-h-[500px]">
            {activeTab === 'listings' && <ListingsTab />}
            {activeTab === 'users' && <UsersTab />}
            {activeTab === 'tokens' && <TokensTab />}
            {activeTab === 'blogs' && <BlogsTab />}
            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'newsletter' && <NewsletterTab />}
            {activeTab === 'pricing' && <PricingTab />}
            {activeTab === 'ai' && <AiSettingsTab />}
            {activeTab === 'support' && <SupportRequestsTab />}
          </div>
        </motion.div>
      </div>
    </div>
  );
}